import { useEffect, useMemo, useState } from 'react';
import { extractToc } from '../../lib/docs';

interface DocsTOCProps {
    content: string;
}

/**
 * Table des matières auto-extraite du Markdown (niveaux h2 + h3).
 * Scroll-spy : met en surbrillance le titre le plus proche du haut de la
 * fenêtre pendant la lecture.
 */
export function DocsTOC({ content }: DocsTOCProps) {
    const entries = useMemo(() => extractToc(content), [content]);
    const [activeId, setActiveId] = useState<string | null>(null);

    useEffect(() => {
        if (entries.length === 0) return;
        const ids = entries.map((e) => e.id);
        const elements = ids
            .map((id) => document.getElementById(id))
            .filter((el): el is HTMLElement => !!el);

        if (elements.length === 0) return;

        const observer = new IntersectionObserver(
            (entriesObs) => {
                const visible = entriesObs
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top);
                if (visible[0]) setActiveId(visible[0].target.id);
            },
            { rootMargin: '-90px 0px -60% 0px', threshold: [0, 0.5, 1] },
        );
        elements.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [entries]);

    if (entries.length === 0) return null;

    return (
        <aside className="docs-toc" aria-label="Sur cette page">
            <div className="docs-toc-head">Sur cette page</div>
            <nav>
                {entries.map((e) => (
                    <a
                        key={e.id}
                        href={`#${e.id}`}
                        className={`docs-toc-link is-h${e.level}${
                            activeId === e.id ? ' is-active' : ''
                        }`}
                    >
                        {e.label}
                    </a>
                ))}
            </nav>
        </aside>
    );
}
